import { computed, inject, Injectable } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Observable, tap } from 'rxjs';
import { AthleteApi } from './athlete.api';
import {
  EMPTY_TRAINING_LEVELS,
  ITrainingLevel,
  ITrainingLevelCategory,
} from './athlete.model';
import {
  customTrainingLevelPlaceholder,
  isUnconfiguredTrainingLevelName,
} from './training-level.utils';

@Injectable({ providedIn: 'root' })
export class TrainingLevelStore {
  private readonly api = inject(AthleteApi);

  private readonly _levels = rxResource({
    stream: () => this.api.getAllTrainingLevels(),
  });

  private readonly customLevels = computed<ITrainingLevel[]>(() =>
    (this._levels.value() ?? EMPTY_TRAINING_LEVELS)
      .filter((level) => !level.is_default_level)
      .sort((a, b) => a.id - b.id),
  );

  readonly isLoading = computed(() => this._levels.isLoading());
  readonly error = computed(() => this._levels.error());

  readonly defaultLevels = computed<ITrainingLevel[]>(() =>
    (this._levels.value() ?? EMPTY_TRAINING_LEVELS).filter((level) => level.is_default_level),
  );

  private readonly slots = computed<ITrainingLevelCategory[]>(() => {
    const levels = this.customLevels();
    if (levels.length === 0) {
      return [];
    }

    const minId = Math.min(...levels.map((level) => level.id));

    return levels.map((level) => ({
      id: level.id,
      name: level.value,
      slotName: customTrainingLevelPlaceholder(level.id - minId + 1),
      isConfigured: !isUnconfiguredTrainingLevelName(level.value),
    }));
  });

  /** Only configured (named) categories — `custom N` slots don't exist yet and are hidden. */
  readonly categories = computed<ITrainingLevelCategory[]>(() =>
    this.slots()
      .filter((slot) => slot.isConfigured)
      .sort((a, b) => a.id - b.id),
  );

  readonly hasAvailableSlot = computed(() => this.slots().some((slot) => !slot.isConfigured));

  readonly firstAvailableSlotId = computed(
    () => this.slots().find((slot) => !slot.isConfigured)?.id ?? null,
  );

  /** Create (rename a `custom N` slot to a real name) OR edit (rename a real category). */
  rename(id: number, value: string): Observable<void> {
    return this.api
      .updateTrainingLevels(this.buildPayload(id, value))
      .pipe(tap(() => this._levels.reload()));
  }

  /** Delete — rename a configured category back to its derived `custom N` slot. */
  resetToSlot(row: ITrainingLevelCategory): Observable<void> {
    return this.rename(row.id, row.slotName);
  }

  reload(): void {
    this._levels.reload();
  }

  private buildPayload(id: number, value: string): ReadonlyArray<{ id: number; value: string }> {
    return this.customLevels().map((level) => ({
      id: level.id,
      value: level.id === id ? value : level.value,
    }));
  }
}
